import React, { useState, useEffect, useId, useMemo, useRef } from 'react';
import { Icon } from './Icon';
import { HistoricalRateModal } from './HistoricalRateModal';
import { useDialog } from '../hooks/useDialog';
import { useCurrency } from '../hooks/useCurrency';
import {
  SUPPORTED_CURRENCIES,
  RATE_MODES,
  getCurrencyConfig,
  getCurrencySettings,
  liveRatesAvailable,
  describeRate,
  setCurrencyCode,
  setManualRate,
  fetchLiveRate,
  fetchHistoricalRate
} from '../utils/currency';
import {
  EARLIEST_RATE_DATE,
  toIsoDate,
  formatLongDate,
  formatRateNumber,
  getInvoiceDateOptions
} from '../utils/historicalRates';

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const weekdayOf = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return WEEKDAY_NAMES[new Date(year, month - 1, day).getDay()];
};

// Header menu for the display currency and, for currencies other than USD,
// the exchange rate used to convert amounts: the live rate, the rate on a
// past date, or one typed in. Also opens the historical rate lookup.
export const CurrencyMenu = ({ invoices }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // The mode whose controls are shown. It becomes the saved mode only once
  // its rate has been loaded, so the dashboard never claims a rate it lacks.
  const [shownMode, setShownMode] = useState('live');
  const [currencyRate, setCurrencyRateInput] = useState('');
  const [rateError, setRateError] = useState('');
  const [rateDate, setRateDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [rateStatus, setRateStatus] = useState('');
  const [isFetchingRate, setIsFetchingRate] = useState(false);
  const titleId = useId();
  const groupName = useId();
  // Dates are applied one at a time, ending with the one chosen last
  const nextDateRef = useRef(null);
  const isApplyingDateRef = useRef(false);

  // Re-render when the currency or its rate changes, here or anywhere else
  useCurrency();
  const currency = getCurrencyConfig().code;
  const invoiceDates = useMemo(() => getInvoiceDateOptions(invoices), [invoices]);

  const closeMenu = () => setIsOpen(false);
  const panelRef = useDialog(isOpen, closeMenu);

  // Re-read saved settings each time the menu opens, so it never shows
  // values that changed in the meantime (for example an auto-refreshed rate).
  useEffect(() => {
    if (!isOpen) return;

    const settings = getCurrencySettings();
    setShownMode(settings.mode);
    setCurrencyRateInput(settings.rate);
    setRateDate(settings.requestedDate || '');
    setRateError('');
    setDateError('');
    setRateStatus('');
  }, [isOpen]);

  const loadLiveRate = async (code) => {
    if (code === 'USD') return;
    setIsFetchingRate(true);
    setRateStatus('Updating rate…');
    try {
      const rate = await fetchLiveRate(code);
      setCurrencyRateInput(String(rate));
      setRateStatus('');
    } catch (err) {
      setRateStatus(err && err.message ? `Failed to fetch live rate: ${err.message}` : 'Failed to fetch live rate');
    } finally {
      setIsFetchingRate(false);
    }
  };

  const loadHistoricalRate = async (code, date) => {
    if (code === 'USD') return;
    setIsFetchingRate(true);
    setRateStatus('Looking up rate…');
    try {
      const result = await fetchHistoricalRate(code, date);
      setCurrencyRateInput(String(result.rate));
      setRateStatus('');
    } catch (err) {
      const latest = err && err.body && err.body.latest;
      setRateStatus(
        latest
          ? `${err.message} The latest available rate is from ${formatLongDate(latest.rateDate)}: 1 USD = ${formatRateNumber(latest.rate)} ${code}.`
          : (err && err.message) || 'Failed to fetch the historical rate'
      );
    } finally {
      setIsFetchingRate(false);
    }
  };

  const handleCurrencyChange = (value) => {
    setCurrencyCode(value);
    setRateError('');
    setRateStatus('');
    if (value === 'USD') return;

    // The saved rate may belong to another currency: load this one's
    const settings = getCurrencySettings();
    if (settings.mode === 'live') loadLiveRate(value);
    else if (settings.mode === 'historical') loadHistoricalRate(value, settings.requestedDate);
  };

  const handleModeChange = (mode) => {
    setShownMode(mode);
    setRateError('');
    setDateError('');
    setRateStatus('');
    if (mode === 'live') {
      loadLiveRate(currency);
    } else if (mode === 'manual') {
      // Keep converting at the rate in use, now as the user's own
      const rate = String(getCurrencyConfig().rate);
      setCurrencyRateInput(rate);
      setManualRate(rate);
    }
  };

  const handleCurrencyRateChange = (e) => {
    const value = e.target.value;
    setCurrencyRateInput(value);
    if (value === '') {
      setRateError('');
      setManualRate('');
      return;
    }
    const parsed = Number(value);
    if (isNaN(parsed) || parsed <= 0) {
      setRateError('Exchange rate must be a number greater than 0.');
      return;
    }
    setRateError('');
    setManualRate(value);
  };

  const today = toIsoDate(new Date());

  // Empty when the date can be looked up
  const describeDateProblem = (date) => {
    if (!isIsoDate(date)) return 'Choose a date.';
    if (date > today) return 'Rates for future dates are not available.';
    if (date < EARLIEST_RATE_DATE) return `Rates are only available from ${formatLongDate(EARLIEST_RATE_DATE)} onwards.`;
    return '';
  };

  // Typing a date passes through other dates on the way (the 2nd before the
  // 20th), so each one waits for the lookup before it and the last one wins.
  const applyHistoricalDate = async (date) => {
    nextDateRef.current = date;
    if (isApplyingDateRef.current) return;
    isApplyingDateRef.current = true;
    while (nextDateRef.current) {
      const next = nextDateRef.current;
      nextDateRef.current = null;
      await loadHistoricalRate(currency, next);
    }
    isApplyingDateRef.current = false;
  };

  // A date picked in the calendar applies straight away. Half-typed dates
  // (year 0002 on the way to 2025) are out of range and wait.
  const handleRateDateChange = (e) => {
    const value = e.target.value;
    setRateDate(value);
    setDateError('');
    if (value && !describeDateProblem(value)) applyHistoricalDate(value);
  };

  const handleRateDateBlur = () => {
    if (rateDate) setDateError(describeDateProblem(rateDate));
  };

  const handleInvoiceDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    setRateDate(value);
    setDateError('');
    applyHistoricalDate(value);
  };

  // The menu closes first so the dialog is the only thing open.
  const openHistory = () => {
    closeMenu();
    setShowHistory(true);
  };

  const isUsd = currency === 'USD';
  const liveAvailable = liveRatesAvailable();
  const dateHint = isIsoDate(rateDate) && !describeDateProblem(rateDate)
    ? `${weekdayOf(rateDate)}, ${formatLongDate(rateDate)}`
    : `Any day from ${formatLongDate(EARLIEST_RATE_DATE)} to today.`;

  return (
    <div className="currency-menu">
      <button
        type="button"
        className="btn-secondary currency-menu-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Display currency: ${currency}`}
        title="Display currency"
      >
        <span className="currency-code">{currency}</span>
        <span className="menu-icon"><Icon name="chevronDown" size={14} /></span>
      </button>

      {isOpen && (
        <>
          {/* Catches clicks outside the menu so they close it and nothing else */}
          <div className="currency-menu-backdrop" onClick={closeMenu} aria-hidden="true" />

          <div
            ref={panelRef}
            className="currency-menu-panel"
            role="dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
          >
            <h2 id={titleId} className="currency-menu-title">Display currency</h2>

            <div className="currency-options" role="radiogroup" aria-labelledby={titleId}>
              {SUPPORTED_CURRENCIES.map((option) => {
                const isSelected = option.code === currency;
                return (
                  <label
                    key={option.code}
                    className={isSelected ? 'currency-option is-selected' : 'currency-option'}
                  >
                    <input
                      type="radio"
                      className="visually-hidden"
                      name={groupName}
                      value={option.code}
                      checked={isSelected}
                      onChange={() => handleCurrencyChange(option.code)}
                      data-autofocus={isSelected ? '' : undefined}
                    />
                    <span>{option.label}</span>
                    {isSelected && <Icon name="check" />}
                  </label>
                );
              })}
            </div>

            {isUsd ? (
              <p className="currency-menu-note">
                Billing data is in USD, so no conversion is applied.
              </p>
            ) : (
              <div className="currency-rate">
                <h3 className="currency-menu-title">Exchange rate</h3>
                <div className="segmented currency-rate-modes" role="group" aria-label="Exchange rate">
                  {RATE_MODES.map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      className={mode.value === shownMode ? 'segment is-active' : 'segment'}
                      aria-pressed={mode.value === shownMode}
                      disabled={mode.value === 'live' && !liveAvailable}
                      onClick={() => handleModeChange(mode.value)}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                {!liveAvailable && (
                  <p className="currency-menu-note">
                    Live rates are not configured on the server. Use a historical or custom rate.
                  </p>
                )}

                {shownMode === 'live' && liveAvailable && (
                  <div className="currency-rate-controls">
                    <button
                      type="button"
                      className="btn-secondary btn-small"
                      onClick={() => loadLiveRate(currency)}
                      disabled={isFetchingRate}
                    >
                      Refresh rate
                    </button>
                  </div>
                )}

                {shownMode === 'historical' && (
                  <div className="currency-rate-controls">
                    <div className="form-group">
                      <label htmlFor="currencyRateDate">Rate date</label>
                      <input
                        id="currencyRateDate"
                        type="date"
                        min={EARLIEST_RATE_DATE}
                        max={today}
                        value={rateDate}
                        onChange={handleRateDateChange}
                        onBlur={handleRateDateBlur}
                        aria-invalid={dateError ? 'true' : undefined}
                        aria-describedby={dateError ? 'currency-date-error' : 'currency-date-hint'}
                      />
                      {dateError ? (
                        <span id="currency-date-error" className="field-error" role="alert">{dateError}</span>
                      ) : (
                        <span id="currency-date-hint" className="form-text">{dateHint}</span>
                      )}
                    </div>
                    {invoiceDates.length > 0 && (
                      <div className="form-group">
                        <label htmlFor="currencyInvoiceDate">Or use an invoice date</label>
                        <select
                          id="currencyInvoiceDate"
                          value={invoiceDates.some((option) => option.value === rateDate) ? rateDate : ''}
                          onChange={handleInvoiceDateChange}
                        >
                          <option value="">Choose an invoice…</option>
                          {invoiceDates.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {shownMode === 'manual' && (
                  <div className="currency-rate-controls">
                    <div className="form-group">
                      <label htmlFor="currencyRate">Exchange rate (1 USD in {currency})</label>
                      <input
                        id="currencyRate"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={currencyRate}
                        onChange={handleCurrencyRateChange}
                        placeholder={currency === 'IDR' ? '15500' : '1'}
                        aria-invalid={rateError ? 'true' : undefined}
                        aria-describedby={rateError ? 'currency-rate-error' : undefined}
                      />
                      {rateError && <span id="currency-rate-error" className="field-error" role="alert">{rateError}</span>}
                    </div>
                  </div>
                )}

                {rateStatus && <p className="rate-status" role="status">{rateStatus}</p>}

                {/* Always the rate in use, whatever is being edited above */}
                <p className="currency-menu-note currency-rate-in-use">{describeRate()}</p>
              </div>
            )}

            <button type="button" className="currency-menu-action" onClick={openHistory}>
              <Icon name="history" />
              Look up a past rate…
            </button>
          </div>
        </>
      )}

      <HistoricalRateModal isOpen={showHistory} onClose={() => setShowHistory(false)} />
    </div>
  );
};
